/**
 * 素材统计页 (Ads Overview)
 *
 * @description 展示游戏素材投放的核心指标、趋势分析、渠道分布、国家分布、素材类型等统计数据
 *
 * @page_sections
 * - 板块 B (MetricCardList): 顶部指标卡片区，展示在投创意数、素材数、新增素材数
 * - 板块 C (TrendChart): 趋势折线图，展示素材规模时间趋势
 * - 板块 D (ChannelAnalysis): 在投广告渠道分析，展示 Top10 渠道投放分布
 * - 板块 E (CountryAnalysis): 在投广告数分国家，展示各国投放分布热力图和趋势
 * - 板块 F (MaterialTypeAnalysis): 素材类型与互动分析，展示素材类型和互动类型分布
 *
 * @filter_support
 * - date: 日期范围筛选（必填）
 * - 自动刷新: 筛选条件变更后，所有板块自动刷新数据
 *
 * @route /AdCreatives/Overview
 * @see specs/003-ads-overview/spec.md
 */
import React, { useMemo } from 'react';
import { Layout } from 'tea-component';
import { useIntl, useLocation } from 'umi';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import CardLoading from '@op/components/CardLoading';
import ContentBody from '@op/components/ContentBody';
import { PAGE_NAME } from '@op/constants/page';
import { useInitBuriedPointInfo } from '@op/operationLog/buriedPoint';
import { AdsOverviewPoint } from '@op/operationLog/pages';
import BubbleCommon, {
  adsOverviewHeadText,
  adsAverageEstimateTips,
} from '@op/components/BubbleCommon';
import BeforeRequest from '@op/components/BeforeRequest';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { useFilterCondConf } from '@op/hooks/useFilterCondConf';
import MetricCardList from './components/MetricCardList';
import TrendChart from './components/TrendChart';
import ChannelAnalysis from './components/ChannelAnalysis';
import CountryAnalysis from './components/CountryAnalysis';
import MaterialTypeAnalysis from './components/MaterialTypeAnalysis';
import moment from 'moment';
import { DBO_TM } from '@op/utils/format';
import './index.scss';
import { adCreativesStopTipsMap } from '@opm/components/BubbleCommon';

const { Content } = Layout;

const defaultFilter = {
  ...DBO_TM.utcFilterRangeDay(13, 0),
  date_type: 'daily',
};

function AdsOverview({ gameInfo, config }: any) {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const locationState = location?.state as
    | { start_time?: string; end_time?: string; date_type?: string }
    | undefined;
  useInitBuriedPointInfo({ buriedPoint: AdsOverviewPoint });

  const userDefaultValue = useMemo(() => {
    // 优先使用从 goto 传来的时间参数（来自多游戏对比页等跳转）
    if (locationState?.start_time && locationState?.end_time) {
      return {
        start_time: locationState.start_time,
        end_time: locationState.end_time,
        date_type: locationState.date_type || 'daily',
      };
    }
    const availableTime = config?.ad_creative_stats;
    if (availableTime) {
      const start_time = moment(availableTime)
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss');
      const end_time = moment(availableTime)
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss');
      return { start_time, end_time, date_type: 'daily' };
    }
    return defaultFilter;
  }, [config, locationState]);

  const { filter, setFilter, loading, ...filterProps } = useFilterCommon({
    keys: ['OpinionMultiDateRange'],
    instanceId: 'Ads_Overview',
  });

  const {
    is_support_ad_creative: isSupportAdCreative,
    materials_config_stats_channels: channelOptions,
    loading: condLoading,
  } = useFilterCondConf({
    gameInfo,
    requestList: [
      { key: 'is_support_ad_creative', defaultValue: false },
      { key: 'materials_config_stats_channels', defaultValue: [] },
    ],
  });

  return (
    <Content className="dbo-page dbo-ads-overview">
      <ContentBody
        horseRaceLampTips={adCreativesStopTipsMap}
        contentHeard={
          <Content.Header
            className="dbo-header"
            title={formatMessage({ id: PAGE_NAME.ADS_OVERVIEW })}
            subtitle={
              <div className="dbo-header-subtitle">
                {formatMessage({ id: 'Data Update' })}
                <BubbleCommon showIcon={false} tipsMap={adsOverviewHeadText}>
                  <TipContext className="title-tip" placement="bottom" />
                </BubbleCommon>
              </div>
            }
          />
        }
      >
        <FilterCommon
          {...filterProps}
          userDefaultValue={userDefaultValue}
          loadingHeight={40}
          extrRequestKeys={['materials_config_region_countries']}
          useOriDefaultValue
          realTimeMode
          filterConfigProps={{
            OpinionMultiDateRange: {
              tips: adsAverageEstimateTips,
              placement: 'right',
            },
          }}
          confirm={(value: any) => {
            setFilter({ ...value, ...gameInfo });
          }}
        />
        <CardLoading loading={loading || condLoading}>
          {/* 板块 B：顶部指标卡片区 */}
          <MetricCardList
            gameInfo={gameInfo}
            filter={filter}
            isSupportAdCreative={isSupportAdCreative}
          />

          {/* 板块 C：趋势折线图 */}
          <TrendChart filter={filter} gameInfo={gameInfo} />

          {/* 板块 D：在投广告渠道分析 */}
          <ChannelAnalysis
            filter={filter}
            gameInfo={gameInfo}
            channelOptions={channelOptions}
          />

          <div className="dbo-ads-overview__title">
            {formatMessage({ id: 'Active Ads Country & Type Analysis' })}
          </div>

          {/* 板块 E：在投广告数分国家 */}
          <CountryAnalysis filter={filter} gameInfo={gameInfo} />

          {/* 板块 F：素材类型与互动分析 */}
          <MaterialTypeAnalysis filter={filter} gameInfo={gameInfo} />
        </CardLoading>
      </ContentBody>
    </Content>
  );
}

export default function Index() {
  return (
    <BeforeRequest ids={['ad_creative_stats']}>
      {(props) => <AdsOverview {...props} />}
    </BeforeRequest>
  );
}
